export const lawyersData = {
  industry: "Lawyers",
  slug: "lawyers",
  
  // SEO Meta Tags
  seo: {
    title: "AI Intake Forms for Law Firms | HeyIntake",
    description: "AI-powered legal intake forms that qualify cases, score leads, and help law firms respond to high-value clients faster. Built for personal injury, family law, immigration & more.",
    keywords: [
      "legal intake form",
      "client intake software for law firms",
      "attorney lead qualification",
      "law firm intake management",
      "case intake software",
      "legal client screening"
    ]
  },

  heroTitle: "Client Intake Forms Built for Legal Practices",
  heroSubtitle: "Capture potential client information, qualify cases faster, and never miss a high-value lead again.",
  heroDescription: "Whether you handle personal injury, family law, immigration, or corporate matters, HeyIntake helps you collect the right information upfront and prioritize your most promising cases.",
  
  // NEW: Why AI Scoring section for word count
  whyAIScoring: {
    title: "Why Law Firms Need AI-Powered Intake",
    intro: "In today's competitive legal market, responding quickly to qualified leads is the difference between winning and losing cases.",
    paragraphs: [
      "Traditional intake processes force attorneys to treat every inquiry the same way, regardless of case value or urgency. This means high-value personal injury cases with clear liability sit in the same queue as tire-kickers and cases outside your practice area. The result? Lost revenue and wasted consultation time.",
      "HeyIntake's AI-powered intake forms change this dynamic. Our adaptive scoring system analyzes each submission in real-time, evaluating factors like case complexity, timeline urgency, and client budget alignment. Within seconds, you know which potential clients deserve immediate attention and which can wait.",
      "For law firms handling personal injury, family law, criminal defense, or business litigation, this means faster response times to serious clients, better case qualification before the initial consultation, and more efficient use of attorney time."
    ]
  },

  painPoints: [
    {
      title: "Unqualified Consultations",
      description: "Spending valuable time on consultations with clients who aren't a good fit for your practice or can't afford your services."
    },
    {
      title: "Missing Critical Case Details",
      description: "Potential clients forget to mention key facts during initial calls, leading to incomplete case assessments."
    },
    {
      title: "Slow Response Times",
      description: "High-value cases go to competitors because you couldn't respond quickly enough to new inquiries."
    },
    {
      title: "Inconsistent Intake Process",
      description: "Different staff members collect different information, creating gaps in your case evaluation."
    },
    {
      title: "No Case Prioritization",
      description: "All inquiries look the same — you can't easily identify which potential cases deserve immediate attention."
    },
    {
      title: "Lost Follow-Up Opportunities",
      description: "Promising leads slip through the cracks because there's no system to track and follow up systematically."
    }
  ],

  exampleFields: [
    {
      category: "Contact Information",
      fields: [
        "Full legal name",
        "Phone number (primary & alternate)",
        "Email address",
        "Preferred contact method",
        "Best time to reach"
      ]
    },
    {
      category: "Case Details",
      fields: [
        "Type of legal matter",
        "Brief description of situation",
        "Date incident occurred",
        "Opposing party information",
        "Have you spoken with other attorneys?"
      ]
    },
    {
      category: "Urgency & Timeline",
      fields: [
        "Court dates or deadlines",
        "Statute of limitations concerns",
        "How urgent is your matter?",
        "Preferred case start date"
      ]
    },
    {
      category: "Financial Information",
      fields: [
        "Estimated budget range",
        "Payment preference (hourly/flat/contingency)",
        "Insurance involvement",
        "Ability to pay retainer"
      ]
    }
  ],

  // Example intake form section
  exampleIntakeForm: {
    title: "Example Legal Intake Form",
    description: "Here's what an effective law firm intake form looks like:",
    fields: [
      { label: "Type of legal matter", type: "select", options: ["Personal Injury", "Family Law", "Criminal Defense", "Business/Corporate", "Estate Planning", "Immigration", "Other"] },
      { label: "Brief description of your situation", type: "textarea" },
      { label: "When did this issue arise?", type: "select", options: ["Within 30 days", "1-6 months ago", "6-12 months ago", "Over 1 year ago"] },
      { label: "Any court dates or deadlines?", type: "select", options: ["Yes - urgent", "Yes - within 30 days", "Yes - within 90 days", "No deadlines"] },
      { label: "Have you consulted other attorneys?", type: "select", options: ["No", "Yes - still deciding", "Yes - need second opinion"] },
      { label: "How do you prefer to pay?", type: "select", options: ["Hourly rate", "Flat fee", "Contingency", "Not sure"] },
      { label: "Budget range for legal fees", type: "select", options: ["$1K-$5K", "$5K-$15K", "$15K-$50K", "$50K+", "Contingency only"] }
    ],
    cta: "Use this exact form → Start for $1"
  },

  // NEW: How It Works section
  howItWorks: {
    title: "How HeyIntake Works for Law Firms",
    subtitle: "From inquiry to qualified consultation in three simple steps",
    steps: [
      {
        title: "Capture Intake Information",
        description: "Potential clients complete your customized intake form with case details, timeline, and budget information."
      },
      {
        title: "AI Scores & Prioritizes",
        description: "Our AI analyzes each submission, scoring cases based on value, urgency, and fit with your practice."
      },
      {
        title: "Focus on Best Cases",
        description: "You receive prioritized leads with AI summaries, so you can respond to high-value cases first."
      }
    ]
  },

  aiScoringExplanation: {
    title: "AI-Powered Case Qualification for Law Firms",
    description: "HeyIntake's AI analyzes each submission to help you prioritize high-value cases and identify potential conflicts or red flags before the consultation.",
    factors: [
      "Case complexity and estimated value",
      "Timeline urgency and statute of limitations",
      "Client's ability to pay or qualify for contingency",
      "Clarity and completeness of case details",
      "Potential conflicts of interest flags"
    ],
    exampleLead: {
      name: "Marcus Johnson",
      context: "Personal Injury • Auto Accident • $50K+ Medical Bills",
      summary: "Clear liability case with documented injuries and ongoing treatment. Defendant has insurance. 18 months until SOL. High-value, recommend priority consultation.",
      score: 92
    }
  },

  useCases: [
    {
      icon: "brain",
      title: "Pre-Qualify Cases Automatically",
      description: "AI scoring identifies high-value cases instantly, so you know which consultations to prioritize."
    },
    {
      icon: "clock",
      title: "Respond to Urgent Matters Fast",
      description: "Get instant alerts when submissions mention court dates, deadlines, or time-sensitive issues."
    },
    {
      icon: "file",
      title: "Collect Documents Upfront",
      description: "Request police reports, medical records, contracts, or other documents before the first meeting."
    },
    {
      icon: "users",
      title: "Standardize Your Intake Process",
      description: "Every potential client provides the same essential information, making case evaluation consistent."
    }
  ],

  // Cross-linking to related industries
  relatedIndustries: [
    { name: "Intake Forms for Accountants", path: "/intake-form-for-accountants" },
    { name: "Intake Forms for Consultants", path: "/intake-form-for-consultants" },
    { name: "Intake Forms for Therapists", path: "/intake-form-for-therapists" }
  ],

  ctaText: "Ready to Qualify More Cases, Faster?"
};
